import { CustomField } from "@/backend/models/customFields/customField";
import { useSubjectIsDisabled } from "@/frontend/hooks/useSubjectIsDisabled";
import { useParameterStore, ValidationStatus } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ParameterSubjectType } from "@/types/parameterTypes";
import { Parameter } from "@/backend/models/parameter";
import { Form } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { StepFailureModal } from "../containers/StepFailureModal";
import { ValidationRunSimplifiedView } from "../validations/ValidationRunSimplifiedView";
import { ValidationsFailedIcon } from "../validations/ValidationsFailedIcon";
import { ValidationsPassingIcon } from "../validations/ValidationsPassingIcon";
import { ViewValidationsButton } from "../validations/ViewValidationsButton";
import { NonPrimitiveParameter } from "./NonPrimitiveParameter";
import { ParameterRunEditModal } from "./ParameterRunEditModal";
import { ParameterInfoTooltip } from "./ReferenceLabel";
import { FileSubjectTypes } from "@/types/fileTypes";
import { FileUploader } from "../files/FileUploader";
import { TruncatedText } from "../cards/TruncatedText";
import { Validation } from "@/backend/models/validation";

export function ParameterItemRunView(props: { parameter: Parameter }) {
  const { parameter } = props;

  return (
    <div id={parameter.id}>
      <div className='d-flex align-items-center gap-2'>
        <ParameterTitleWithTooltip parameter={parameter} />
      </div>
        <ParameterItemRunValue parameter={parameter} />
    </div>
  );
}

export function ParameterTitleWithTooltip(props: { parameter: Parameter }) {
  const { parameter } = props;
  return (
    <Form.Label className='d-flex align-items-center gap-1'>
      {parameter.title}
      <ParameterInfoTooltip consumerId={parameter.id} description={parameter.description} />
    </Form.Label>
  );
}

function ParameterItemRunValue(props: { parameter: Parameter }) {
  const { parameter } = props;
  const validationStatuses = useParameterStore(useShallow(state => state.getValidationStatusesForSubject(parameter.id)));
  const step = useTreeStore(useShallow(state => state.containers.find(c => c.id === parameter.subjectId)));
  const parentDisabled = useSubjectIsDisabled(parameter.subjectId, parameter.subjectType);
  const validations = useParameterStore(useShallow(state => state.getValidationForSubject(parameter.id)));

  if (!parameter.isPrimitive()) {
    return <NonPrimitiveParameter parameter={parameter} disabled runView />;
  }

  let containerClass = '';
  if (validationStatuses.some(v => v === ValidationStatus.FAILING)) {
    containerClass = 'border border-danger rounded p-2 text-danger';
  } else if (validationStatuses.some(v => v === ValidationStatus.OVERIDDEN)) {
    containerClass = 'border border-warning rounded p-2 text-muted';
  }

  const customField = parameter.fields[0] as CustomField;
  const textFieldTypes = [CUSTOM_FIELD_TYPES.String, CUSTOM_FIELD_TYPES.MultilineText, CUSTOM_FIELD_TYPES.Integer, CUSTOM_FIELD_TYPES.Float, CUSTOM_FIELD_TYPES.Boolean];

  return (
    <div className={`d-flex gap-2 justify-content-between ${containerClass}`}>
      <div className='d-flex align-items-start gap-2'>
        { customField.fieldType === CUSTOM_FIELD_TYPES.File &&
          <FileUploader subjectId={parameter.fields[0].id} subjectType={FileSubjectTypes.CUSTOM_FIELD} hideFilePreview refetchSubject={() => {}} file={parameter.fields[0].file} readOnly isInput={false} />
        }
        { textFieldTypes.includes(customField.fieldType) &&
          <p className='font-monospace small px-2 py-1 mb-0 bg-light border rounded shadow-sm' style={customField.fieldType === CUSTOM_FIELD_TYPES.MultilineText ? { whiteSpace: 'pre-wrap' } : undefined}>{customField.asString() || I18n.get('NULL')}</p>
        }
        <ValidationsSummary parameter={parameter} validations={validations} />
      </div>
      { (validationStatuses.some(v => v === ValidationStatus.FAILING) && parameter.subjectType === ParameterSubjectType.STEP && !parentDisabled) && (
        <div className='d-flex align-items-center gap-2'>
          <ParameterRunEditModal subjectId={parameter.subjectId} parameterType={parameter.type} buttonLabel={I18n.get('override')} buttonClassName='btn-warning' />
          <StepFailureModal stepId={step?.id as string} />
        </div>
      )}
    </div>
  );
}

function ValidationsSummary(props: { parameter: Parameter, validations: Validation[] }) {
  const { parameter, validations } = props;
  const validationStatuses = useParameterStore(useShallow(state => state.getValidationStatusMapForSubject(parameter.id)));

  if (validations.length === 0) return null;

  const allValidationsPassing = Array.from(validationStatuses.values()).every(v => v === ValidationStatus.PASSING);
  const failingValidations = validations.filter((v) => validationStatuses.get(v.id) === ValidationStatus.FAILING);
  const validationToShow = failingValidations.length > 0 ? failingValidations[0] : validations[0];

  if (allValidationsPassing) return <ValidationsPassingIcon />;

  return (
    <div className='d-flex align-items-center gap-2'>
      <ValidationRunSimplifiedView
        key={validationToShow.id}
        validation={validationToShow}
        showButtons={false}
      />
      { validations.length > 1 && <ViewValidationsButton parameter={parameter} placement='right' /> }
      { failingValidations.length > 0 && <>
        <ValidationsFailedIcon />
        <div className='d-flex flex-column align-items-start'>
          <TruncatedText
            key={failingValidations[0].id}
            text={failingValidations[0].failMessage || ''}
            maxLength={100}
            className='text-dark'
          />
        </div>
      </> }
    </div>
  );
}